import { useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AdminTable, type ColDef } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { ChangeSalesPriceModal } from "@/components/admin/shared/ChangeSalesPriceModal";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import {
  PageHeader,
  AddButton,
  SaveButton,
  CancelButton,
  ViewButton,
  EditButton,
  DeleteButton,
} from "@/components/admin/shared/PageHeader";
import { TablePagination } from "@/components/admin/shared/TablePagination";
import { TableSearch } from "@/components/admin/shared/TableSearch";
import { PennField, PennInput, PennDropdown } from "@/components/ui";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import {
  useAutorivConfiguratorItem,
  useAutorivConfiguratorItemOptions,
  useAutorivConfiguratorItems,
  useCreateAutorivConfiguratorItem,
  useDeleteAutorivConfiguratorItem,
  useUpdateAutorivConfiguratorItem,
} from "@/hooks/useAutorivConfiguratorItems";
import {
  useApplyDatasetPriceUpdate,
  useDatasetPriceUpdateSummary,
} from "@/hooks/usePriceUpdate";
import {
  AUTORIV_BLANK_FILTER,
  type AutorivConfiguratorItem,
  type UpdateAutorivConfiguratorItemBody,
} from "@/api/autorivConfiguratorItems";
import { CURRENCY_SYMBOL, formatMoney, type Currency } from "@/utils/dataset-money";
import { Percent } from "lucide-react";

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

/**
 * The row carries EUR and USD prices side by side; this page is mounted once
 * per currency (EU route -> price_eur, US route -> price_usd) exactly like the
 * Pemserter EUR/USD pair. Everything except the price column is shared.
 */
const CURRENCY_META: Record<
  Currency,
  {
    symbol: string;
    label: string;
    /** Region this mount belongs to — currency travels via region (§D.14). */
    region: string;
    /** `region.region_id` the bulk price change is scoped to. */
    regionId: number;
  }
> = {
  // Separators are NOT configured here — `formatMoney` renders both currencies
  // en-US style ("€54,726.19"). A per-page separator table is what let this
  // page drift to de-DE ("€54.726,19") while Sherex Options rendered en-US.
  EUR: { symbol: CURRENCY_SYMBOL.EUR, label: "Price (EUR)", region: "EU", regionId: 1 },
  USD: { symbol: CURRENCY_SYMBOL.USD, label: "Price (USD)", region: "NA", regionId: 2 },
};

/**
 * `item_description` is imported as `human copy | Feeder; Studs; 1 track(s)`.
 * Everything from the first "|" onwards repeats the Subcategory / Fastener
 * Group / Tracks columns, so the admin UI shows and edits only the left side —
 * the same split the customer configurator applies in `cleanMdsItemDescription`.
 */
const descriptionHead = (value: string | null): string =>
  value === null ? "" : (value.split("|")[0] ?? "").trim();

/** The hidden `| …` remainder, kept verbatim so an edit never destroys it. */
const descriptionTail = (value: string | null): string => {
  if (value === null) return "";
  const pipe = value.indexOf("|");
  return pipe === -1 ? "" : value.slice(pipe).trim();
};

/** Trailing-zero-trimmed force value, e.g. "12.00" -> "12". */
const formatForce = (value: string | null): string => {
  if (value === null || value.trim() === "") return "—";
  return value.includes(".")
    ? value.replace(/0+$/, "").replace(/\.$/, "")
    : value;
};

const itemFormSchema = z.object({
  partNo: z.string().trim().min(1, "Part No is required").max(30),
  product: z.string().trim().min(1, "Product is required").max(20),
  itemName: z.string().trim().min(1, "Item Name is required").max(100),
  itemDescription: z.string().trim().max(200).optional(),
  subcategory: z.string().trim().max(20).optional(),
  fastenerGroup: z.string().trim().max(50).optional(),
  feederTracks: z.string().trim().max(10).optional(),
  /**
   * Whole kN only. The column is numeric(8,2) and every stored value is a
   * whole number (22, 30, 45, …), so the admin surface steps by 1 rather than
   * inviting a fractional rating that nothing downstream expects.
   */
  forceKn: z
    .union([
      z.literal(""),
      z.coerce
        .number()
        .int("Must be a whole number")
        .nonnegative("Must be non-negative"),
    ])
    .optional(),
  /** Bound to price_eur or price_usd depending on the mounted currency. */
  price: z.coerce
    .number({ invalid_type_error: "Price is required" })
    .nonnegative("Must be greater than or equal to 0"),
});
type ItemFormData = z.infer<typeof itemFormSchema>;

const blank: ItemFormData = {
  partNo: "",
  product: "",
  itemName: "",
  itemDescription: "",
  subcategory: "",
  fastenerGroup: "",
  feederTracks: "",
  forceKn: "",
  price: 0,
};

/**
 * Toolbar filter. Styled as a native select rather than `PennDropdown` so it
 * matches the search input beside it in both themes.
 */
const FilterSelect = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) => (
  <select
    aria-label={`Filter by ${label}`}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={[
      "rounded-lg border bg-white dark:bg-gray-900 px-3 py-2 text-body focus:border-penn-500 focus:ring-1 focus:ring-penn-500 outline-none",
      value
        ? "border-penn-500 text-blue_grey"
        : "border-gray-200 dark:border-gray-700 text-blue_grey2",
    ].join(" ")}
  >
    <option value="">All {label}</option>
    {options.map((option) => (
      <option key={option} value={option}>
        {option}
      </option>
    ))}
    {/* A third of the rows carry no subcategory / fastener group, so the
        blank bucket needs its own reachable filter value. */}
    <option value={AUTORIV_BLANK_FILTER}>(No value)</option>
  </select>
);

/**
 * A row saved before a value left the catalog must still render its own value,
 * otherwise opening the modal would silently blank the field.
 */
const withCurrent = (values: string[], current: string | null): string[] =>
  current && current !== "" && !values.includes(current)
    ? [...values, current]
    : values;

const toOptions = (values: string[]) =>
  values.map((value) => ({ value, label: value }));

/**
 * Attribute dropdown for the modal. Blank is a real, selectable choice — these
 * columns are nullable and plenty of rows legitimately have no value.
 */
const attributeOptions = (values: string[], current: string | null) => [
  { value: "", label: "— None —" },
  ...toOptions(withCurrent(values, current)),
];

const ProductBadge = ({ value }: { value: string | null }) => {
  if (!value) return <>—</>;
  return (
    <span className="inline-flex items-center rounded-full bg-purple-50 dark:bg-purple-900/30 px-3 py-0.5 text-purple-700 dark:text-purple-300 text-small-medium">
      {value}
    </span>
  );
};

const SubcategoryBadge = ({ value }: { value: string | null }) => {
  if (!value) return <>—</>;
  return (
    <span className="inline-flex items-center rounded-full bg-indigo-50 dark:bg-indigo-900/30 px-3 py-0.5 text-indigo-700 dark:text-indigo-300 text-small-medium">
      {value}
    </span>
  );
};

export const AutorivConfiguratorItemsPage = ({
  title = "Autoriv Configurator Items (EU)",
  subtitle = "Dataset: Autoriv Configurator Items (EU)",
  currency = "EUR",
}: {
  title?: string;
  subtitle?: string;
  /** Which price column this mount reads and writes. EUR is the default. */
  currency?: Currency;
}) => {
  const priceKey = currency === "USD" ? "priceUsd" : "priceEur";
  const {
    symbol: currencySymbol,
    label: priceLabel,
    region: regionName,
    regionId,
  } = CURRENCY_META[currency];
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const { searchInput, setSearchInput, search, clearSearch } = useDebouncedSearch(() => setPageIndex(0));
  /** "" = no filter; AUTORIV_BLANK_FILTER = rows with no value. */
  const [subcategoryFilter, setSubcategoryFilter] = useState("");
  const [fastenerGroupFilter, setFastenerGroupFilter] = useState("");

  const params = useMemo(
    () => ({
      page: pageIndex + 1,
      limit: pageSize,
      search: search || undefined,
      subcategory: subcategoryFilter || undefined,
      fastenerGroup: fastenerGroupFilter || undefined,
    }),
    [pageIndex, pageSize, search, subcategoryFilter, fastenerGroupFilter],
  );

  const { data, isLoading, isFetching, isError, error } =
    useAutorivConfiguratorItems(params);
  const rows: AutorivConfiguratorItem[] = data?.data ?? [];
  const pagination = data?.pagination;
  const total = pagination?.total ?? 0;
  const hasNext = pagination ? pagination.page < pagination.totalPages : false;

  const optionsQuery = useAutorivConfiguratorItemOptions();
  const productOptions = optionsQuery.data?.products ?? [];
  const subcategoryOptions = optionsQuery.data?.subcategories ?? [];
  const fastenerGroupOptions = optionsQuery.data?.fastenerGroups ?? [];
  const feederTrackOptions = optionsQuery.data?.feederTracks ?? [];

  type ModalMode = "add" | "edit" | "view";
  type ModalState = { mode: ModalMode; item?: AutorivConfiguratorItem } | null;

  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] =
    useState<AutorivConfiguratorItem | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Bulk "% change bulk price" dialog state. Both currencies sit on the same
  // item row, so only this mount's price column moves; history goes to the
  // shared price_update_log, tagged with this dataset + region.
  const [priceChangeOpen, setPriceChangeOpen] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const priceSummary = useDatasetPriceUpdateSummary(
    "autoriv-configurator-items",
    regionId,
    priceChangeOpen,
  );
  const applyPriceMutation = useApplyDatasetPriceUpdate("autoriv-configurator-items");

  const applyPriceChange = (percentage: number, direction: "increase" | "decrease") => {
    setBulkError(null);
    applyPriceMutation.mutate(
      { percentage, direction, regionId },
      {
        onSuccess: () => setPriceChangeOpen(false),
        onError: (e) => setBulkError(apiErrorMessage(e, "Failed to apply price change.")),
      },
    );
  };

  const editingId = modal?.mode !== "add" ? modal?.item?.id ?? null : null;
  const detail = useAutorivConfiguratorItem(editingId);
  const createMutation = useCreateAutorivConfiguratorItem();
  const updateMutation = useUpdateAutorivConfiguratorItem();
  const deleteMutation = useDeleteAutorivConfiguratorItem();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<ItemFormData>({ resolver: zodResolver(itemFormSchema) });

  const fillForm = (item: AutorivConfiguratorItem) => {
    reset({
      partNo: item.partNo ?? "",
      product: item.product ?? "",
      itemName: item.itemName ?? "",
      itemDescription: descriptionHead(item.itemDescription),
      subcategory: item.subcategory ?? "",
      fastenerGroup: item.fastenerGroup ?? "",
      feederTracks: item.feederTracks ?? "",
      forceKn: item.forceKn === null ? "" : Number(item.forceKn),
      price: item[priceKey] === null ? 0 : Number(item[priceKey]),
    });
  };

  const openAdd = () => {
    setFormError(null);
    reset(blank);
    setModal({ mode: "add" });
  };
  const openEdit = (item: AutorivConfiguratorItem) => {
    setFormError(null);
    fillForm(item);
    setModal({ mode: "edit", item });
  };
  const openView = (item: AutorivConfiguratorItem) => {
    setFormError(null);
    fillForm(item);
    setModal({ mode: "view", item });
  };
  const closeModal = () => {
    setModal(null);
    setFormError(null);
    reset(blank);
  };

  const onSubmit = (form: ItemFormData) => {
    if (!modal || modal.mode === "view") return;
    setFormError(null);

    // The form only ever holds the visible head; re-attach the row's hidden
    // "| …" tail so saving an edit does not silently drop stored metadata.
    // Clearing the field clears the column outright, tail included.
    const descHead = form.itemDescription?.trim() ?? "";
    const descTail =
      modal.mode === "edit" && modal.item
        ? descriptionTail(modal.item.itemDescription)
        : "";
    const itemDescription =
      descHead === "" ? null : descTail ? `${descHead} ${descTail}` : descHead;

    if (itemDescription !== null && itemDescription.length > 200) {
      setFormError(
        'Item Description is too long — the stored value, including the hidden "|" suffix, must be 200 characters or fewer.',
      );
      return;
    }

    const body = {
      partNo: form.partNo.trim(),
      itemName: form.itemName.trim(),
      itemDescription,
      product: form.product.trim(),
      subcategory: form.subcategory?.trim() || null,
      fastenerGroup: form.fastenerGroup?.trim() || null,
      feederTracks: form.feederTracks?.trim() || null,
      forceKn:
        form.forceKn === "" || form.forceKn === undefined
          ? null
          : String(form.forceKn),
    };
    const price = String(form.price);
    /** Only this mount's currency is ever sent — the sibling price column is
     *  owned by the other page and must not be written from here. */
    const pricePatch =
      currency === "USD" ? { priceUsd: price } : { priceEur: price };

    if (modal.mode === "edit" && modal.item) {
      // PATCH carries only what actually changed — an untouched field is left
      // alone server-side (notably the other currency's price column).
      const current = modal.item;
      const patch: UpdateAutorivConfiguratorItemBody = {};
      if (body.partNo !== (current.partNo ?? "")) patch.partNo = body.partNo;
      if (body.itemName !== (current.itemName ?? "")) patch.itemName = body.itemName;
      // Compare heads only: an untouched description sends nothing, so the
      // stored string keeps its original spacing around the "|".
      if (descHead !== descriptionHead(current.itemDescription))
        patch.itemDescription = body.itemDescription;
      if (body.product !== (current.product ?? "")) patch.product = body.product;
      if (body.subcategory !== current.subcategory)
        patch.subcategory = body.subcategory;
      if (body.fastenerGroup !== current.fastenerGroup)
        patch.fastenerGroup = body.fastenerGroup;
      if (body.feederTracks !== current.feederTracks)
        patch.feederTracks = body.feederTracks;
      if (Number(body.forceKn ?? NaN) !== Number(current.forceKn ?? NaN))
        patch.forceKn = body.forceKn;
      if (Number(price) !== Number(current[priceKey] ?? NaN))
        Object.assign(patch, pricePatch);

      if (Object.keys(patch).length === 0) {
        closeModal();
        return;
      }

      updateMutation.mutate(
        { id: current.id, body: patch },
        {
          onSuccess: closeModal,
          onError: (e) =>
            setFormError(apiErrorMessage(e, "Failed to save configurator item.")),
        },
      );
    } else {
      createMutation.mutate(
        { ...body, ...pricePatch },
        {
          onSuccess: closeModal,
          onError: (e) =>
            setFormError(
              apiErrorMessage(e, "Failed to create configurator item."),
            ),
        },
      );
    }
  };

  const isView = modal?.mode === "view";
  const saving = createMutation.isPending || updateMutation.isPending;

  const cols: ColDef<AutorivConfiguratorItem>[] = [
    {
      header: "Part No",
      width: "min-w-[7rem]",
      render: (i) => (
        <span className="whitespace-nowrap">{i.partNo ?? "—"}</span>
      ),
    },
    { header: "Item Name", render: (i) => i.itemName ?? "—" },
    {
      header: "Item Description",
      render: (i) => descriptionHead(i.itemDescription) || "—",
    },
    { header: "Product", render: (i) => <ProductBadge value={i.product} /> },
    {
      header: "Subcategory",
      render: (i) => <SubcategoryBadge value={i.subcategory} />,
    },
    { header: "Fastener Group", render: (i) => i.fastenerGroup ?? "—" },
    { header: "Tracks", render: (i) => i.feederTracks ?? "—" },
    { header: "Force", render: (i) => formatForce(i.forceKn) },
    {
      header: priceLabel,
      render: (i) => (
        <span className="whitespace-nowrap tabular-nums">
          {formatMoney(i[priceKey], currency)}
        </span>
      ),
    },
    {
      header: "Actions",
      render: (i) => (
        <div className="flex space-x-2">
          <ViewButton onClick={() => openView(i)} />
          <EditButton onClick={() => openEdit(i)} />
          <DeleteButton
            onClick={() => setDeleteTarget(i)}
            disabled={deleteMutation.isPending}
            disabledTitle="Delete in progress"
          />
        </div>
      ),
    },
  ];

  // Region is part of the modal title so the admin can never lose track of
  // which mount (EU / NA price column) they are writing to.
  const titleMap: Record<ModalMode, string> = {
    add: `Add Autoriv configurator item (${regionName})`,
    edit: `Edit Autoriv configurator item (${regionName})`,
    view: `View Autoriv configurator item (${regionName})`,
  };

  return (
    <>
      <PageHeader
        title={title}
        subtitle={`${subtitle} · ${total} records`}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => { setBulkError(null); setPriceChangeOpen(true); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-penn-500 hover:bg-penn-700 active:bg-penn-500 text-white rounded-lg shadow-sm shadow-penn-100 dark:shadow-penn-900/40 transition-all duration-150 cursor-pointer text-body-medium"
            >
              <Percent size={15} />
              Change Bulk Price
            </button>
            <AddButton onClick={openAdd} label="Add record" />
          </div>
        }
      />

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          Failed to load configurator items
          {error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      {deleteError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          {deleteError}
        </div>
      )}

      <TableSearch
        value={searchInput}
        onChange={setSearchInput}
        placeholder="Search part no"
        showClear={Boolean(searchInput || subcategoryFilter || fastenerGroupFilter)}
        onClear={() => {
          clearSearch();
          setSubcategoryFilter("");
          setFastenerGroupFilter("");
          setPageIndex(0);
        }}
      >
        <FilterSelect
          label="Subcategory"
          value={subcategoryFilter}
          options={subcategoryOptions}
          onChange={(value) => {
            setSubcategoryFilter(value);
            setPageIndex(0);
          }}
        />
        <FilterSelect
          label="Fastener Group"
          value={fastenerGroupFilter}
          options={fastenerGroupOptions}
          onChange={(value) => {
            setFastenerGroupFilter(value);
            setPageIndex(0);
          }}
        />
      </TableSearch>

      <AdminTable
        columns={cols}
        rows={rows}
        keyExtractor={(i) => i.id}
        loading={isLoading}
      />

      <TablePagination
        pageIndex={pageIndex}
        pageSize={pageSize}
        rowsOnPage={rows.length}
        hasNext={hasNext}
        busy={isFetching}
        onPrev={() => setPageIndex((p) => Math.max(0, p - 1))}
        onNext={() => setPageIndex((p) => p + 1)}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPageIndex(0);
        }}
      />

      <AdminModal
        open={modal !== null}
        onClose={closeModal}
        title={modal ? titleMap[modal.mode] : ""}
        maxWidth="max-w-2xl"
        footer={
          isView ? (
            <CancelButton onClick={closeModal} />
          ) : (
            <>
              <CancelButton onClick={closeModal} />
              <SaveButton
                onClick={saving ? undefined : handleSubmit(onSubmit)}
                label={saving ? "Saving…" : "Save"}
              />
            </>
          )
        }
      >
        {modal?.mode !== "add" && detail.isLoading ? (
          <div className="py-10 text-center text-blue_grey4 text-body">Loading…</div>
        ) : (
          <form
            className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            onSubmit={handleSubmit(onSubmit)}
          >
            {formError && (
              <div className="col-span-1 sm:col-span-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-body">
                {formError}
              </div>
            )}

            <PennField
              label="Part No"
              htmlFor="partNo"
              required={!isView}
              error={!isView ? errors.partNo?.message : undefined}
            >
              <PennInput
                id="partNo"
                placeholder="e.g. P-ZFW01"
                disabled={isView}
                error={!isView && !!errors.partNo}
                {...register("partNo")}
              />
            </PennField>

            <PennField
              label="Product"
              htmlFor="product"
              required={!isView}
              error={!isView ? errors.product?.message : undefined}
            >
              <Controller
                control={control}
                name="product"
                render={({ field }) => (
                  <PennDropdown
                    inputId="product"
                    options={toOptions(
                      withCurrent(productOptions, modal?.item?.product ?? null),
                    )}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    isDisabled={isView}
                    placeholder="Select product..."
                  />
                )}
              />
            </PennField>

            <div className="col-span-1 sm:col-span-2">
              <PennField
                label="Item Name"
                htmlFor="itemName"
                required={!isView}
                error={!isView ? errors.itemName?.message : undefined}
              >
                <PennInput
                  id="itemName"
                  placeholder="e.g. Feeding system A000 – A1 (A100)"
                  disabled={isView}
                  error={!isView && !!errors.itemName}
                  {...register("itemName")}
                />
              </PennField>
            </div>

            <div className="col-span-1 sm:col-span-2">
              <PennField
                label="Item Description"
                htmlFor="itemDescription"
                error={!isView ? errors.itemDescription?.message : undefined}
              >
                <PennInput
                  id="itemDescription"
                  placeholder="Short description line (optional)"
                  disabled={isView}
                  error={!isView && !!errors.itemDescription}
                  {...register("itemDescription")}
                />
              </PennField>
            </div>

            <PennField
              label="Subcategory"
              htmlFor="subcategory"
              error={!isView ? errors.subcategory?.message : undefined}
            >
              <Controller
                control={control}
                name="subcategory"
                render={({ field }) => (
                  <PennDropdown
                    inputId="subcategory"
                    options={attributeOptions(
                      subcategoryOptions,
                      modal?.item?.subcategory ?? null,
                    )}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    isDisabled={isView}
                    placeholder="— None —"
                  />
                )}
              />
            </PennField>

            <PennField
              label="Fastener Group"
              htmlFor="fastenerGroup"
              error={!isView ? errors.fastenerGroup?.message : undefined}
            >
              <Controller
                control={control}
                name="fastenerGroup"
                render={({ field }) => (
                  <PennDropdown
                    inputId="fastenerGroup"
                    options={attributeOptions(
                      fastenerGroupOptions,
                      modal?.item?.fastenerGroup ?? null,
                    )}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    isDisabled={isView}
                    placeholder="— None —"
                  />
                )}
              />
            </PennField>

            <PennField
              label="Feeder Tracks"
              htmlFor="feederTracks"
              error={!isView ? errors.feederTracks?.message : undefined}
            >
              <Controller
                control={control}
                name="feederTracks"
                render={({ field }) => (
                  <PennDropdown
                    inputId="feederTracks"
                    options={attributeOptions(
                      feederTrackOptions,
                      modal?.item?.feederTracks ?? null,
                    )}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    isDisabled={isView}
                    placeholder="— None —"
                  />
                )}
              />
            </PennField>

            <PennField
              label="Force (kN)"
              htmlFor="forceKn"
              error={!isView ? errors.forceKn?.message : undefined}
            >
              <PennInput
                id="forceKn"
                type="number"
                step="1"
                min="0"
                placeholder="optional"
                disabled={isView}
                error={!isView && !!errors.forceKn}
                {...register("forceKn")}
              />
            </PennField>

            <PennField
              label={priceLabel}
              htmlFor="price"
              required={!isView}
              error={!isView ? errors.price?.message : undefined}
            >
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-blue_grey4 text-body">
                  {currencySymbol}
                </span>
                <PennInput
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  className="pl-7"
                  disabled={isView}
                  error={!isView && !!errors.price}
                  {...register("price")}
                />
              </div>
            </PennField>
          </form>
        )}
      </AdminModal>

      <ChangeSalesPriceModal
        open={priceChangeOpen}
        onClose={() => setPriceChangeOpen(false)}
        title={`% Change Bulk Price — Autoriv Configurator Items (${regionName})`}
        currency={currency}
        scopeLabel={`Autoriv Configurator Items (${regionName})`}
        recordCount={priceSummary.data?.totalRecords ?? 0}
        recordNoun={{ one: "price", many: "prices" }}
        descriptionSuffix={`Only the ${currency} column moves; items with no ${currency} price stay empty.`}
        applying={applyPriceMutation.isPending}
        error={bulkError}
        onApply={applyPriceChange}
        lastUpdate={priceSummary.data?.last}
        loadingSummary={priceSummary.isLoading}
      />

      <DeleteConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          setDeleteError(null);
          deleteMutation.mutate(deleteTarget.id, {
            onError: (e) =>
              setDeleteError(
                apiErrorMessage(e, "Failed to delete configurator item."),
              ),
            onSettled: () => setDeleteTarget(null),
          });
        }}
        itemLabel={deleteTarget?.partNo ?? undefined}
      />
    </>
  );
};
